import React, { useState } from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { NumberField } from "@/components/simulator/NumberField";
import { fmtEUR } from "@/lib/format";
import { Plus, Trash2, Layers, Package } from "lucide-react";
import { toast } from "sonner";

export const FutureLotsTab = () => {
  const { inputs, summary, updateFutureLot, addFutureLot, removeFutureLot } = useSimulator();
  const lots = inputs.future_lots || [];

  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const handleAdd = () => {
    if (!newName.trim()) {
      toast.error("Inserisci un nome per il nuovo lotto");
      return;
    }
    addFutureLot({
      name: newName.trim(),
      description: newDesc.trim(),
      surface_m2: 200,
      annual_revenue: 0,
      annual_opex: 0,
      capex: 0,
      enabled: true,
    });
    setNewName("");
    setNewDesc("");
    toast.success(`Lotto "${newName}" aggiunto`);
  };

  const totals = summary.futureLots;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-violet-500/10 border border-violet-500/30 p-3">
            <Layers className="h-7 w-7 text-violet-400" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-100">
              Lotti Futuri — Sezione Modulare
            </h2>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Aggiungi, modifica o disattiva lotti successivi. Ogni lotto attivo entra automaticamente nel calcolo di EBITDA e proiezione 5Y.
            </p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryStat label="Lotti attivi" value={totals.count} accent="text-violet-300" testId="future-lots-count" />
        <SummaryStat label="Ricavi annui aggregati" value={fmtEUR(totals.revenue)} accent="text-emerald-300" testId="future-lots-revenue" />
        <SummaryStat label="OPEX annui aggregati" value={fmtEUR(totals.opex)} accent="text-rose-300" testId="future-lots-opex" />
        <SummaryStat label="EBITDA lotti futuri" value={fmtEUR(totals.ebitda)} accent="text-sky-300" testId="future-lots-ebitda" />
      </div>

      {/* Lots list */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {lots.map((lot) => (
          <Card
            key={lot.id}
            data-testid={`future-lot-${lot.id}`}
            className={`bg-slate-900/70 border p-5 space-y-4 transition-all ${
              lot.enabled ? "border-slate-700" : "border-slate-800 opacity-60"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Package className="h-5 w-5 text-violet-400 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <Input
                    data-testid={`future-lot-${lot.id}-name`}
                    value={lot.name}
                    onChange={(e) => updateFutureLot(lot.id, { name: e.target.value })}
                    className="font-display font-semibold text-slate-100 bg-transparent border-slate-800 h-8 text-base"
                  />
                  <Textarea
                    data-testid={`future-lot-${lot.id}-description`}
                    value={lot.description || ""}
                    onChange={(e) => updateFutureLot(lot.id, { description: e.target.value })}
                    placeholder="Descrizione..."
                    className="mt-2 text-xs bg-slate-950 border-slate-800 text-slate-400 min-h-[50px]"
                  />
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">Attivo</span>
                  <Switch
                    data-testid={`future-lot-${lot.id}-enabled`}
                    checked={lot.enabled}
                    onCheckedChange={(v) => updateFutureLot(lot.id, { enabled: v })}
                  />
                </div>
                <Button
                  data-testid={`future-lot-${lot.id}-delete`}
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    removeFutureLot(lot.id);
                    toast.success(`Lotto "${lot.name}" rimosso`);
                  }}
                  className="h-8 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <NumberField
                label="Superficie"
                value={lot.surface_m2}
                onChange={(v) => updateFutureLot(lot.id, { surface_m2: v })}
                min={50}
                max={2000}
                step={10}
                unit="m²"
                testId={`future-lot-${lot.id}-surface`}
                sliderTheme="violet-slider"
              />
              <NumberField
                label="CAPEX totale"
                value={lot.capex}
                onChange={(v) => updateFutureLot(lot.id, { capex: v })}
                min={0}
                max={2000000}
                step={5000}
                unit="€"
                testId={`future-lot-${lot.id}-capex`}
                sliderTheme="violet-slider"
              />
              <NumberField
                label="Ricavo annuo stimato"
                value={lot.annual_revenue}
                onChange={(v) => updateFutureLot(lot.id, { annual_revenue: v })}
                min={0}
                max={2000000}
                step={2000}
                unit="€ / anno"
                testId={`future-lot-${lot.id}-revenue`}
                sliderTheme="algae-slider"
              />
              <NumberField
                label="OPEX annua stimata"
                value={lot.annual_opex}
                onChange={(v) => updateFutureLot(lot.id, { annual_opex: v })}
                min={0}
                max={1000000}
                step={2000}
                unit="€ / anno"
                testId={`future-lot-${lot.id}-annual-opex`}
                sliderTheme="saffron-slider"
              />
            </div>

            <div className="pt-3 border-t border-slate-800 flex items-center justify-between">
              <span className="text-xs text-slate-400">EBITDA lotto</span>
              <span
                data-testid={`future-lot-${lot.id}-ebitda`}
                className={`font-mono-tab font-bold ${lot.enabled ? "text-sky-300" : "text-slate-500"}`}
              >
                {fmtEUR(lot.annual_revenue - lot.annual_opex)}
              </span>
            </div>
          </Card>
        ))}
      </div>

      {/* Add lot form */}
      <Card className="bg-slate-900/70 border-slate-800 border-dashed p-6">
        <h3 className="font-display text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Plus className="h-4 w-4 text-violet-400" />
          Aggiungi nuovo lotto
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Nome lotto</Label>
            <Input
              data-testid="new-lot-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="es. Lotto 6 — Erbe officinali"
              className="bg-slate-950 border-slate-800 text-slate-100"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">Descrizione</Label>
            <Input
              data-testid="new-lot-description-input"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Ipotesi produttiva..."
              className="bg-slate-950 border-slate-800 text-slate-100"
            />
          </div>
        </div>
        <Button
          data-testid="add-future-lot-button"
          onClick={handleAdd}
          className="mt-4 bg-violet-500 hover:bg-violet-600 text-white"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          Aggiungi lotto
        </Button>
      </Card>
    </div>
  );
};

const SummaryStat = ({ label, value, accent, testId }) => (
  <Card data-testid={testId} className="bg-slate-900/70 border-slate-800 p-4">
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-1.5 font-display font-extrabold text-2xl font-mono-tab ${accent}`}>{value}</div>
  </Card>
);
